import type {
  CSSProperties,
  HTMLAttributeAnchorTarget,
  MouseEvent,
  ReactNode,
} from 'react';
import { useMemo } from 'react';
import { Link, NavLink, useLocation } from 'react-router';

import { HORIZON_ENABLED } from '#/rendering/state/renderingState';
import { getDataAndAriaProps } from '#/utils/getDataAndAriaProps';

import { useVersionedRouterLinkClick } from './useVersionedRouterLinkClick';

type Props = {
  children?: ReactNode;
  id?: string;
  className?: string;
  navLink?: boolean;
  to: string;
  state?: Record<string, unknown>;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  tabIndex?: number;
  target?: HTMLAttributeAnchorTarget;
  title?: string;
  style?: CSSProperties;
  qaId?: string;
};

export function VersionedRouterLink({
  children,
  id,
  className,
  navLink,
  to,
  onClick,
  state,
  tabIndex,
  target,
  title,
  style,
  qaId,
  ...dataAndAriaProps
}: Props) {
  const location = useLocation();

  // Multi-router routing: ensure routing to external routes is handled by the browser
  const isExternalRoute = useMemo(
    () =>
      (!location.pathname.startsWith('/cms') && to.startsWith('/cms')) ||
      (!location.pathname.startsWith('/staff') && to.startsWith('/staff')) ||
      (location.pathname.startsWith('/cms') && !to.startsWith('/cms')) ||
      (location.pathname.startsWith('/staff') && !to.startsWith('/staff')),
    [location, to],
  );

  const { normalizedTo, deferredAppLinkClick } = useVersionedRouterLinkClick({
    to,
    state,
    onClick,
    target,
    reloadDocument: isExternalRoute,
  });

  const Component = navLink ? NavLink : Link;

  return (
    <Component
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...getDataAndAriaProps(dataAndAriaProps)}
      id={id}
      className={className}
      to={normalizedTo}
      onClick={deferredAppLinkClick}
      state={state}
      discover={HORIZON_ENABLED ? 'none' : undefined}
      reloadDocument={isExternalRoute}
      target={target}
      tabIndex={tabIndex}
      title={title}
      style={style}
      data-qa-id={qaId}
    >
      {children}
    </Component>
  );
}
